<template>
  <div
    v-infinite-scroll="autoLoadMore ? loadMore : undefined"
    infinite-scroll-disabled="busy"
    class="rk-table"
    :class="[
      { 'rk-table--border_x': isBorderX },
      { 'rk-table--border_y': isBorderY },
      { 'rk-table--hidden_x': lockDir === 'x' },
      { 'rk-table--hidden_y': lockDir === 'y' },
      { 'rk-table--stripe': stripe }
    ]"
    @touchstart="touchstart"
    @touchmove="touchmove"
    @touchend="touchend"
  >
    <div style="display: none">
      <slot></slot>
    </div>
    <div class="rk-table__wrapper">
      <table-header
        v-if="showHeader"
        :has-edge-padding="headerSplitLine"
        :class="headerSplitLine ? 'header-split' : ''"
        :store="store"
        @sort-change="sortChange"
        @resize-width="updateColumnWidth"
      />
      <table-body
        :has-edge-padding="headerSplitLine"
        :store="store"
        @row-click="rowClick"
        @row-dblclick="rowDblclick"
      />
    </div>
    <div class="rk-table__footer">
      <template v-if="finished">
        <div v-if="!data.length" class="rk-table__footer--finished" style="min-height: 600px">
          <slot name="empty">
            <div class="empty">
              <img src="@/assets/images/noData@2x.png" />
              <span class="label">{{ $t('暂无数据') }}</span>
            </div>
          </slot>
        </div>
      </template>
      <template v-else>
        <div v-if="loading" class="rk-table__footer--loading">
          {{ $t('正在加载') }}
          <i class="iconfont icon-jiazai--kuai"></i>
        </div>
      </template>

      <template v-if="showFooter">
        <slot name="footer">
          <template v-if="!finished">
            <div v-if="!loading" class="rk-table__footer--loading" @click="loadMore">
              {{ $t('显示更多') }}
              <i class="iconfont icon-xiangxia"></i>
            </div>
          </template>
          <template v-else>
            <div v-if="data.length" class="rk-table__footer--finished">{{ $t('没有更多了') }}</div>
          </template>
        </slot>
      </template>
    </div>
  </div>
</template>

<script>
import infiniteScroll from 'vue-infinite-scroll'
import TableHeader from './table-header.vue'
import TableBody from './table-body.vue'
export default {
  name: 'RkTable',
  directives: {
    infiniteScroll
  },
  components: { TableHeader, TableBody },
  props: {
    autoLoadMore: {
      type: Boolean,
      default: true
    },
    data: {
      type: Array,
      default: () => []
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    defaultSort: {
      type: Object,
      default() {
        return { prop: '', order: 'desc' }
      }
    },
    isBorderX: {
      type: Boolean,
      default: false
    },
    isBorderY: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    finished: {
      type: Boolean,
      default: true
    },
    highlight: {
      type: Boolean,
      default: false
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    stripe: {
      type: Boolean,
      default: false
    },
    headerSplitLine: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      columns: [],
      sort: this.defaultSort,
      currentRow: null,
      lockDir: null,
      deactivated: false
    }
  },
  computed: {
    store() {
      return {
        data: this.data,
        columns: this.columns,
        sort: this.sort,
        currentRow: this.currentRow
      }
    },
    busy() {
      return this.loading || this.finished || this.deactivated
    }
  },
  activated() {
    this.deactivated = false
  },
  deactivated() {
    this.deactivated = true
  },
  mounted() {
    this.getColumns()
  },
  methods: {
    // 锁定滚动方向
    touchstart(e) {
      this.lockDir = null
      const { clientX, clientY } = e.changedTouches[0] ?? {}
      this.touchX = clientX
      this.touchY = clientY
    },
    touchmove(e) {
      if (this.isMove) return
      this.isMove = true
      const { clientX, clientY } = e.changedTouches[0] ?? {}
      const diffX = clientX - this.touchX
      const diffY = clientY - this.touchY
      if (Math.abs(diffX) > Math.abs(diffY)) {
        this.lockDir = 'y'
      } else {
        this.lockDir = 'x'
      }
    },
    touchend() {
      this.isMove = false
    },
    updateColumnWidth(width, index) {
      this.columns = this.columns.map((item, i) => {
        if (i === index) {
          item.width = width + 'px'
        }
        return item
      })
    },
    getColumns() {
      const childrens = this.$slots.default
      const columnConponents =
        childrens?.filter((item) => {
          return item.tag && item.componentOptions && item.componentOptions.Ctor.options.name === 'RkTableColumn'
        }) ?? []
      this.columns = columnConponents.map((item) => {
        const { label, prop, width, align, fixed, sortable, $scopedSlots } = item.componentInstance
        const column = {
          label: this.$t(label) || label,
          prop,
          width: this.parseWidth(width, fixed),
          align,
          fixed: fixed === true ? 'left' : fixed,
          sortable,
          renderCell: null,
          headerSlot: null
        }
        let renderCell = column.renderCell
        column.renderCell = function (createElement, data) {
          if ($scopedSlots.default) {
            renderCell = () => $scopedSlots.default(data)
          } else {
            renderCell = function () {
              const { row } = data
              const prop = column.prop
              return row[prop]
            }
          }
          return renderCell()
        }
        let headerSlot = column.headerSlot
        column.headerSlot = function (createElement, data) {
          if ($scopedSlots.header) {
            headerSlot = () => $scopedSlots.header(data)
          } else {
            headerSlot = function () {
              return column.label
            }
          }
          return headerSlot()
        }
        return column
      })
    },
    parseWidth(width, fixed) {
      const unit = width?.replace(/[0-9.]/gi, '') || 'px'
      let value = parseFloat(width, 10)
      if (isNaN(value)) {
        value = null
      }
      if (fixed && !value) {
        value = 100
      }
      return value ? value + unit : value
    },
    sortChange(data) {
      this.sort = data
      this.$emit('sort-change', data)
    },
    rowClick(row, index) {
      if (this.highlight) {
        this.currentRow = index
      }
      this.$emit('row-click', row, index)
    },
    rowDblclick(row) {
      this.$emit('row-dblclick', row)
    },
    loadMore() {
      if (this.busy) return
      this.$emit('load')
    }
  }
}
</script>

<style lang="scss">
.rk-table {
  /* 表格背景色 */
  --table-thead-background-color: var(--bg-color-2);
  --table-tbody-background-color: var(--bg-color-2);
  /* 单元格内边距 */
  --table-thead-cell-padding: 8px 4px;
  /* 单元格内边距 */
  --table-tbody-cell-padding: 9px 4px;
  // 表格边框颜色
  --table-border-color: var(--border-color);
  /* 表头字体颜色 */
  --table-thead-font-color: var(--font-color-3);
  /* 表体字体颜色 */
  --table-tbody-font-color: var(--font-color-1);
  /* 表头字体大小 */
  --table-thead-font-size: 14px;
  /* 表体字体大小 */
  --table-tbody-font-size: 14px;
  /* 排序图标常规色 */
  --table-normal-sort-color: #acb2c0;
  /* 排序图标激活色 */
  --table-active-sort-color: var(--main-color-1);
  /* 高亮行背景色 */
  --highlight-row-color: transparent;
  /* 高亮行背景色 */
  --table-tbody-word-break: break-all;
  // 斑马线颜色
  --table-stripe-color: var(--bg-color-2);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.rk-table {
  overflow: auto;
  user-select: none;
  height: 100%;
  .rk-table__wrapper {
    display: inline-flex;
    flex-direction: column;
    min-width: 100%;
  }

  .rk-table__header {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  .rk-table__row {
    display: flex;
    width: 100%;
  }

  .rk-table__row--header {
    font-size: var(--table-thead-font-size);
    color: var(--table-thead-font-color);

    .rk-table__column {
      position: relative;
      padding: var(--table-thead-cell-padding);
      background: var(--table-thead-background-color);
    }

    .rk-table__column--sortable {
      cursor: pointer;
    }
  }

  .rk-table__row--body {
    font-size: var(--table-tbody-font-size);
    color: var(--table-tbody-font-color);
    .rk-table__column {
      padding: var(--table-tbody-cell-padding);
      word-break: var(--table-tbody-word-break);
      background: var(--table-tbody-background-color);
    }
  }

  .rk-table__row--highlight {
    .rk-table__column {
      background: var(--highlight-row-color);
    }
  }

  .rk-table__column {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    overflow: hidden;
  }

  .rk-table__column--left {
    text-align: left;
    justify-content: flex-start;
  }
  .rk-table__column--right {
    text-align: right;
    justify-content: flex-end;
  }
  .rk-table__column--center {
    text-align: center;
    justify-content: center;
  }
}

.rk-table--border_x {
  .rk-table__column {
    border-bottom: 1px solid var(--table-border-color);
  }
}

.rk-table--border_y {
  border: 1px solid var(--table-border-color);
  .rk-table__column:not(:last-child) {
    border-right: 1px solid var(--table-border-color);
  }
}

.rk-table--hidden_x {
  overflow-x: hidden;
}

.rk-table--hidden_y {
  overflow-y: hidden;
}

.rk-table--stripe {
  .rk-table__row--body {
    &:nth-child(2n) {
      .rk-table__column {
        background: var(--table-stripe-color);
      }
    }
  }
}

.rk-table__footer {
  position: sticky;
  left: 0;
  font-size: 12px;
  background-color: var(--table-tbody-background-color);
  .rk-table__footer--loading,
  .rk-table__footer--finished {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 0;
  }
  .rk-table__footer--loading {
    color: var(--table-active-sort-color);
  }
  .rk-table__footer--finished {
    color: var(--table-thead-font-color);
  }

  .rk-table__footer--loading {
    .iconfont {
      margin-left: 10px;
      font-size: 12px;
    }
    .icon-jiazai--kuai {
      animation: rotating 2s linear infinite;
    }
    @keyframes rotating {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }
}

.empty {
  display: flex;
  flex-direction: column;
  align-content: center;

  img {
    width: 160px;
    height: 160px;
  }

  .label {
    padding-top: 25px;
    font-size: 16px;
    color: #778691;
    text-align: center;
  }
}
</style>
